import React from 'react';
import { Navigate, useOutletContext } from 'react-router';
import { SEOHelmet } from 'components/SEO';
import { playPath } from 'utils/play/inviteUrl';
import PlayNotFound from '../../PlayNotFound';
import { PlayLayout } from '../../layout/PlayLayout';
import { playerTabs } from '../../layout/playTabs';
import type { TournamentOutletContext } from '../../PlayRoutes';
import { useTournament } from '../../hooks/useTournament';
import { StandingsTable } from '../../components/StandingsTable';
import { TournamentLoading } from './TournamentHomePage';

export const standingsSubtitle = (rounds: { startedAt: string | null }[], resultsPending: boolean): string => {
  const played = rounds.filter((r) => r.startedAt).length;
  if (played === 0) return 'Before round 1';
  return resultsPending ? `During round ${played}` : `After round ${played}`;
};

/** /play/:code/standings for players (Decision 23). The TO reads the same table inside the console. */
const StandingsPage: React.FC = () => {
  const { code } = useOutletContext<TournamentOutletContext>();
  const { state, isLoading, notFound } = useTournament(code);

  if (notFound) return <PlayNotFound reason="missing" />;
  if (isLoading || !state) return <TournamentLoading />;
  if (state.viewer.role === 'TO') return <Navigate replace to={`${playPath(code, 'host')}?tab=standings`} />;
  if (state.viewer.role !== 'PLAYER') return <Navigate replace to={playPath(code)} />;

  const pending = !!state.currentRound && state.currentRound.resultsIn < state.currentRound.resultsTotal;
  return (
    <PlayLayout title={state.tournament.name} subtitle="Standings" tabs={playerTabs(code, 'standings', state.tournament.decklistRequired)}>
      <SEOHelmet title={`${state.tournament.name} - Standings - TradeMagic Play`} description="Tournament standings." noindex />
      <StandingsTable rows={state.standings} variant="player" subtitle={standingsSubtitle(state.rounds, pending)} />
    </PlayLayout>
  );
};

export default StandingsPage;
